import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { FastField, Field, Form } from 'formik';

import {
  BaseBox,
  BaseBoxHeading,
  BaseButtonFill,
  BaseHint,
  BaseRadioGroup,
  InputField,
  SelectField,
  SingleSelectArrayField,
} from 'bxcommon';
import config from 'bxcommon/app.config';
import { DEFAULT_COMPANY } from './constants';

import './CompanyInformation.scss';
import { AMENDMENT_TYPE, EMPLOYER_CHANGE_TYPE } from 'bxcommon/constants/status.constant';

const { maxFileSize } = config;

const getStepHeaderKey = (isEmployerChange, employerChangeType, isNewInEmployerChange) => {
  if (!isEmployerChange) {
    return 'request.form.companyInformation.heading';
  }

  if (employerChangeType === EMPLOYER_CHANGE_TYPE.CHANGE_OF_EMPLOYER) {
    return 'request.form.newCompanyInformation.heading';
  }

  return isNewInEmployerChange ? 'request.form.additionalCompanyInformation.heading' : 'request.form.existingCompanyInformation.heading';
}

export const CompanyInformation = (props) => {
  const { t } = useTranslation();
  const {
    values,
    countries,
    employmentTypes,
    ombudsmanOptions,
    renderUploadBox,
    FILE_UPLOAD_TYPES,
    index,
    cleanFieldsAndFiles,
    formikHelpers,
    saveStep,
    borderless,
    isRenewal,
    isEmployerChange,
  } = props;

  const onAdd = async (showFirst = false, isEmployerChange = false) => {
    if (showFirst) {
      values.companies.unshift(DEFAULT_COMPANY);
    } else {
      values.companies.push(DEFAULT_COMPANY);
    }

    // HINT: request below is performed for the needs of obtaining ID for a new company object.
    // COMPANY.ID is needed during uploading files for assigning file to a specific company object.
    const result = await saveStep(values);
    const sortFn = showFirst ? (a, b) => b.id - a.id : (a, b) => a.id - b.id; // sort to have the new company on top or bottom

    const companiesWithIsNewMarker = result.companies.toSorted(sortFn).map((company, idx) => {
      // The new company is at index 0 if showFirst, otherwise at the last index
      const isNewCompany = showFirst ? idx === 0 : idx === result.companies.length - 1;
      return {
        ...company,
        is_new_in_employer_change: isEmployerChange && isNewCompany,
      };
    });

    formikHelpers.form.setFieldValue('companies', companiesWithIsNewMarker);
  };

  const onRemove = async () => {
    await cleanFieldsAndFiles('confirmation_of_employment_files', index);
    await cleanFieldsAndFiles('liability_insurance_files', index);
    formikHelpers.remove(index);
    values.companies.splice(index, 1);
    // HINT: request below is performed for keeping the consistent behavior between adding and removing companies
    const result = await saveStep(values);
    formikHelpers.form.setFieldValue('companies', result.companies);
  };

  const extraFieldsMapper = {
    company_id: (_, request) => values.companies[index].id || request.companies[index].id,
  };

  const stepHeaderKey = getStepHeaderKey(isEmployerChange, values.employer_change_type, values.companies[index].is_new_in_employer_change);

  const showFooterButtonsOnTop =
    isEmployerChange && values.employer_change_type === EMPLOYER_CHANGE_TYPE.ADDITIONAL_EMPLOYER;

  const footerButtons = (
    <div className="company-information__footer-buttons">
      <BaseButtonFill
        className={classnames({
          'd-none': values.companies.length === 1 && index === 0,
        })}
        type="button"
        iconLeft
        icon="delete"
        data-testid="request-form-additional-provider"
        onClick={() => onRemove()}
      >
        {t('request.form.jobDescription.removeJobDescription')}
      </BaseButtonFill>

      <BaseButtonFill
        className={classnames({
          'd-none': showFooterButtonsOnTop ? index !== 0 : values.companies.length - 1 !== index,
        })}
        type="button"
        iconLeft
        icon="add"
        data-testid="request-form-additional-provider"
        onClick={() => onAdd(showFooterButtonsOnTop, isEmployerChange)}
      >
        {t('request.form.jobDescription.addJobDescription')}
      </BaseButtonFill>
    </div>
  );

  return (
    <BaseBox borderless={borderless} data-testid="request-form-box-company" lifted className="request-wrapper">
      <BaseBoxHeading className="request-wrapper__main-heading">{t(stepHeaderKey)}</BaseBoxHeading>
      {showFooterButtonsOnTop && footerButtons}
      <Form className="form-container">
        {values.companies[index].id && (
          <FastField type="hidden" name={`companies.${index}.id`} value={values.companies[index].id} />
        )}
        <div className="col-12">
          <Field
            component={BaseRadioGroup}
            name={`companies.${index}.type_of_employment`}
            options={employmentTypes}
            value={values.companies[index].type_of_employment}
            data-testid="type_of_employment"
          />
        </div>
        <div className="row">
          <div className="col-12">
            <div className="row request-form--with-hint">
              <InputField name={`companies.${index}.name`} label={t('request.form.companyInformation.company_name')} />
              <BaseHint>{t('request.form.hints.companyInformation')}</BaseHint>
            </div>
            <div className="row">
              <InputField name={`companies.${index}.address_line_1`} label={t('request.form.common.address_line_1')} />
            </div>
            <div className="row">
              <InputField name={`companies.${index}.address_line_2`} label={t('request.form.common.address_line_2')} />
            </div>
            <div className="row">
              <div className="col-4">
                <InputField name={`companies.${index}.postcode`} label={t('request.form.common.postcode')} />
              </div>
              <div className="col-8">
                <InputField name={`companies.${index}.city`} label={t('request.form.common.city')} />
              </div>
            </div>
            <div className="row">
              <SelectField
                name={`companies.${index}.country`}
                label={t('request.form.common.country')}
                options={countries}
                placeholder={t('request.form.personalInformation.countryPlaceholder')}
                popupClassName="company-country"
              />
            </div>
          </div>
        </div>
      </Form>

      <BaseBoxHeading>{t('request.form.jobDescription.heading')}</BaseBoxHeading>
      <Form className="form-container">
        <div className="col-12">
          <div className="row request-form--with-hint">
            <InputField
              name={`companies.${index}.job_function`}
              label={t('request.form.jobDescription.job_function')}
            />
            <BaseHint>{t('request.form.hints.jobFunction')}</BaseHint>
          </div>
          <div className="row request-form--with-hint">
            <InputField
              name={`companies.${index}.job_position`}
              label={t('request.form.jobDescription.job_position')}
            />
            <BaseHint>{t('request.form.hints.jobPosition')}</BaseHint>
          </div>
        </div>
      </Form>

      <BaseBoxHeading>{t('request.form.ombudsmanOffice.heading')}</BaseBoxHeading>
      <Form className="form-container">
        <div className="col-12">
          <div className="row request-form--with-hint">
            <SingleSelectArrayField
              name={`companies.${index}.ombudsman_offices`}
              label={t('request.form.ombudsmanOffice.ombudsman_offices')}
              options={ombudsmanOptions}
              placeholder={t('request.form.common.selectPlaceholder')}
              popupClassName="company-ombudsman-offices"
            />
            <BaseHint>
              <Trans i18nKey="request.form.hints.ombudsmanList">
                <a
                  href="https://www.regservices.ch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link--underlined"
                >
                  www.regservices.ch
                </a>
              </Trans>
            </BaseHint>
          </div>
        </div>
      </Form>

      <BaseBoxHeading>{t('request.form.uploadDocuments.heading')}</BaseBoxHeading>
      <Form className="form-container">
        <div className="col-12">
          <div className="row request-form--with-hint flex-column">
            {renderUploadBox({
              name: `companies.${index}.confirmation_of_employment_files`,
              title: t('request.form.uploadDocuments.confirmationOfEmployment'),
              conditions: t('request.form.uploadDocuments.fileDefaults'),
              type: FILE_UPLOAD_TYPES.CONFIRMATION_OF_EMPLOYMENT_FILE,
              maxFileSize,
              isRenewal: isRenewal,
              config: {
                extraFieldsMapper,
                pdfOnly: false,
              },
            })}
            <BaseHint>
              <Trans i18nKey="request.form.hints.confirmationOfEmployment">
                <a
                  href="https://www.regservices.ch/?download=7462"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link--underlined"
                >
                  Employer Confirmation
                </a>
              </Trans>
            </BaseHint>
          </div>
          <div className="row request-form--with-hint">
            {renderUploadBox({
              name: `companies.${index}.liability_insurance_files`,
              title: t('request.form.uploadDocuments.liabilityInsuranceFiles'),
              conditions: t('request.form.uploadDocuments.fileDefaults'),
              type: FILE_UPLOAD_TYPES.LIABILITY_INSURANCE_FILE,
              maxFileSize,
              isRenewal: isRenewal,
              config: {
                extraFieldsMapper,
                pdfOnly: false,
              },
            })}
            <BaseHint>
              {t('request.form.hints.liabilityInsurance')}
              <ul>
                <li>{t('request.form.hints.liabilityInsurance1')}</li>
                <li>{t('request.form.hints.liabilityInsurance2')}</li>
                <li>{t('request.form.hints.liabilityInsurance3')}</li>
              </ul>
            </BaseHint>
          </div>
          {isEmployerChange && (
            <div className="row request-form--with-hint flex-column" style={{ marginTop: '12rem' }}>
              {renderUploadBox({
                name: `companies.${index}.document_of_affiliation_files`,
                title: t('request.form.uploadDocuments.documentOfAffiliationFiles'),
                conditions: t('request.form.uploadDocuments.fileDefaults'),
                type: FILE_UPLOAD_TYPES.DOCUMENT_OF_AFFILIATION_FILE,
                maxFileSize,
                isRenewal: isRenewal,
                config: {
                  extraFieldsMapper,
                  pdfOnly: false,
                },
              })}
              <BaseHint>{t('request.form.hints.documentOfAffiliation')}</BaseHint>
            </div>
          )}
        </div>
      </Form>

      {!showFooterButtonsOnTop && footerButtons}
    </BaseBox>
  );
};

export default CompanyInformation;
